import { useEffect, useMemo, useState } from "react";
import { ExternalLink, AlertTriangle, Plus, Pencil, Trash2, Eye } from "lucide-react";
import { fetchMaterials, fetchJobs, deleteMaterial, formatApiError } from "@/lib/api";
import { PageHeader, RangeToggle, useRange, fmtKr, safeNum } from "@/components/dashboard/Primitives";
import SearchInput from "@/components/dashboard/SearchInput";
import MaterialFormDialog from "@/components/dashboard/MaterialFormDialog";
import MaterialDetailModal from "@/components/dashboard/MaterialDetailModal";
import DataSourceBadge from "@/components/dashboard/DataSourceBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_FILTERS = [
    { v: "all", l: "Alle" },
    { v: "low", l: "Lavt lager" },
    { v: "ok", l: "OK" },
];

export default function Materials() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [range, setRange] = useRange("month");
    const [editing, setEditing] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [openDetail, setOpenDetail] = useState(null);

    const isAdmin = user && (user.role === "owner" || user.role === "admin");

    useEffect(() => {
        fetchMaterials().then((d) => setItems(d || [])).catch((e) => toast.error(formatApiError(e)));
        fetchJobs().then((d) => setJobs(d || [])).catch(() => {});
    }, []);

    const jobDetails = useMemo(() => {
        const m = {};
        jobs.forEach((j) => { m[j.id] = j; });
        return m;
    }, [jobs]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        return items.filter((m) => {
            const stock = safeNum(m.stock);
            const min = safeNum(m.min_qty);
            if (statusFilter === "low" && stock > min) return false;
            if (statusFilter === "ok" && stock <= min) return false;
            if (!q) return true;
            return [m.id, m.name, m.supplier, m.shelf].some((v) => (v || "").toString().toLowerCase().includes(q));
        });
    }, [items, search, statusFilter]);

    const low = items.filter((m) => safeNum(m.stock) <= safeNum(m.min_qty));

    const onSaved = (saved) => {
        setItems((prev) => {
            const idx = prev.findIndex((p) => p.id === saved.id);
            if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
            return [...prev, saved];
        });
    };

    const onDelete = async (m) => {
        if (!window.confirm(`Slet "${m.name}"?`)) return;
        try {
            await deleteMaterial(m.id);
            setItems((prev) => prev.filter((x) => x.id !== m.id));
            toast.success("Materiale slettet");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid="materials-page" className="p-5 sm:p-8 max-w-7xl mx-auto">
            <PageHeader
                title="Materials"
                subtitle="Materialekatalog med lagerstatus, leverandører og forbrugsstatistik."
                right={
                    <div className="flex flex-wrap items-center gap-3">
                        <DataSourceBadge entity="materials" />
                        <SearchInput value={search} onChange={setSearch} placeholder="Søg materiale, varenr., leverandør, hylde…" testid="materials-search" />
                        <RangeToggle value={range} onChange={setRange} testid="materials-range" />
                        <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 shadow-sm">
                            {STATUS_FILTERS.map((f) => (
                                <button key={f.v} data-testid={`materials-filter-${f.v}`} onClick={() => setStatusFilter(f.v)} className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${statusFilter === f.v ? "bg-blue-600 text-white" : "text-slate-600 hover:text-slate-900"}`}>{f.l}</button>
                            ))}
                        </div>
                        {isAdmin && (
                            <Button data-testid="materials-create-btn" onClick={() => { setEditing(null); setOpenForm(true); }} className="rounded-full h-10 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow">
                                <Plus className="w-4 h-4 mr-1.5" /> Opret materiale
                            </Button>
                        )}
                    </div>
                }
            />

            {low.length > 0 && (
                <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                        <p className="text-sm font-semibold text-amber-900">{low.length} materialer har lavt lager</p>
                        <p className="text-xs text-amber-700 mt-0.5">{low.map((m) => m.name).join(", ")}</p>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                            <tr>
                                <th className="px-4 py-3 text-left">Materiale</th>
                                <th className="px-4 py-3 text-left">Hylde</th>
                                <th className="px-4 py-3 text-right">Lager</th>
                                <th className="px-4 py-3 text-right">Min.</th>
                                <th className="px-4 py-3 text-right">Køb / Salg</th>
                                <th className="px-4 py-3 text-left">Leverandør</th>
                                <th className="px-4 py-3 text-left">Status</th>
                                <th className="px-4 py-3 text-right">Handling</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filtered.map((m) => {
                                const stock = safeNum(m.stock);
                                const min = safeNum(m.min_qty);
                                const isLow = stock <= min;
                                return (
                                    <tr key={m.id} data-testid={`material-${m.id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => setOpenDetail(m)}>
                                        <td className="px-4 py-3 font-medium text-slate-900">{m.name || "Ukendt"}</td>
                                        <td className="px-4 py-3 text-slate-600 font-mono text-xs">{m.shelf || "—"}</td>
                                        <td className={`px-4 py-3 text-right font-semibold ${isLow ? "text-red-600" : "text-slate-700"}`}>{stock} <span className="text-xs text-slate-400 font-normal">{m.unit || "stk"}</span></td>
                                        <td className="px-4 py-3 text-right text-slate-500">{min} <span className="text-xs text-slate-400">{m.unit || "stk"}</span></td>
                                        <td className="px-4 py-3 text-right text-slate-700">{fmtKr(m.buy_price)} / <span className="text-emerald-600 font-semibold">{fmtKr(m.sell_price)}</span></td>
                                        <td className="px-4 py-3 text-slate-600">
                                            {m.link ? (
                                                <a href={m.link} target="_blank" rel="noreferrer" data-testid={`material-link-${m.id}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium">
                                                    {m.supplier || "Link"} <ExternalLink className="w-3 h-3" />
                                                </a>
                                            ) : (m.supplier || "—")}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full ${isLow ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>{isLow ? "Lavt lager" : "OK"}</span>
                                        </td>
                                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="inline-flex gap-1.5">
                                                <Button size="sm" variant="outline" data-testid={`material-view-${m.id}`} onClick={() => setOpenDetail(m)} className="h-8 text-xs"><Eye className="w-3.5 h-3.5 mr-1" />Se</Button>
                                                {isAdmin && (
                                                    <>
                                                        <Button size="sm" variant="outline" data-testid={`material-edit-${m.id}`} onClick={() => { setEditing(m); setOpenForm(true); }} className="h-8 text-xs"><Pencil className="w-3.5 h-3.5" /></Button>
                                                        <Button size="sm" variant="outline" data-testid={`material-delete-${m.id}`} onClick={() => onDelete(m)} className="h-8 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">{search ? "Ingen materialer matcher." : "Ingen data for valgt periode"}</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <MaterialFormDialog open={openForm} material={editing} onClose={() => { setOpenForm(false); setEditing(null); }} onSaved={onSaved} />
            <MaterialDetailModal material={openDetail} jobs={jobs} jobDetails={jobDetails} open={!!openDetail} onClose={() => setOpenDetail(null)} />
        </div>
    );
}
